/* vp-copy-4.js -- copy + behaviour overrides for the Vegan Protein builder
   arm (claude-4).

   ONLY loaded by templates/product.vegan-protein-builder-russ-claude-4.json.

   Everything here exists because the source lives in assets/vp-pdp-2.js, the
   engine SHARED by claude-2, claude-4 and claude-5. Editing the engine would
   move all three arms at once, and it is a ~55KB file that themeFilesUpsert can
   only replace wholesale.

   ---- 1: the add-to-cart label ---------------------------------------
   The engine sets it per render as:
       empty ? "select at least 1 bag" : (focused.preorder ? "pre-order" : "add to cart")

   WHY SYNCHRONOUS: done from a debounced setTimeout it flashed "pre-order" for
   a frame, because setTimeout is a MACROTASK and the browser may paint first.
   MutationObserver callbacks are MICROTASKS -- they drain before paint.

   ---- 2: per-flavour badges, in ONE or TWO places ---------------------
       #vpChips  .vb-tag   pill on the flavour row
       #vpStage  .vb       pill over the big product image
   A chip has TWO .vp-tags containers: span.nmwrap (inline, after the name) and
   span.vp-rowctl (top-right, above the add button). The engine uses .vp-rowctl;
   a plain querySelector returns .nmwrap, hence the scoped lookup.

   ---- 3: whole subscribe card is clickable ---------------------------
   #vpOptSub is only the header row -- 74px of a 448px card. Clicks anywhere in
   the card now delegate to it. Bound ONCE to .vp-info, not the card, because
   the engine re-creates the card on every render.

   ---- 4: the perk lists ----------------------------------------------
   ONE-TIME is replaced wholesale by ONCE_MISSING (crossed-out items only).
   SUBSCRIBE comes from Loop's plan description, so it is only reordered
   (SUB_ORDER) and appended to (SUB_EXTRA). The reorder is conditional on the
   pair being the wrong way round -- moving a node unconditionally would
   re-trigger the observer forever.

   ---- 5: empty state scrolls to the flavour list ---------------------
   With nothing chosen, both CTAs read "select at least 1 bag" and the engine
   marks them disabled. A DISABLED BUTTON FIRES NO EVENTS -- not on itself, not
   on ancestors -- so tapping it did nothing at all, which reads as broken when
   the button is telling you to go and do something.

   vp-builder-4.css sets pointer-events:none on those buttons while disabled so
   the click reaches an ancestor and this document listener. The button KEEPS
   its disabled attribute: styling, tab order and screen-reader semantics are
   unchanged; only hit-testing moves.

   HOW THE CTA IS IDENTIFIED (a bug worth recording): the first version used
   document.elementsFromPoint and looked for #vpAtc / #vpStickyBtn in the stack.
   That can NEVER match -- elementsFromPoint honours pointer-events, so the very
   property that lets the click through also removes the button from hit-test
   results. The click passed through correctly and the handler ignored it. The
   test is plain geometry instead: is the click coordinate inside the button's
   bounding rect. Unaffected by pointer-events, and works for both the inline
   CTA and the fixed sticky bar.

   WHY THE SCROLL IS NOT BLINDLY SMOOTH: behavior:"smooth" is driven by the same
   animation clock as requestAnimationFrame, so it does nothing at all while the
   tab is hidden -- the call silently no-ops and the page never moves. That is
   normally invisible (a real shopper's tab is visible), but it also means a
   background/inactive tab, a reduced-motion preference, or any browser without
   smooth support would leave the button feeling dead again. So the smooth
   attempt is verified: if the page has not started moving shortly after, it
   falls back to an instant jump. prefers-reduced-motion skips straight to
   instant, which is what that setting asks for. */
(function(){
  "use strict";

  var FROM = /^\s*pre-?order\s*$/i;
  var TO = "add to cart";

  /* where: "both" = flavour chip AND hero image, "hero" = hero image only.
     cls drives the colour rule in vp-builder-4.css.

     CAREFUL: pick() below takes the LAST matching entry, not the first. A
     broad pattern sitting lower in this list silently overrides a specific one
     above it -- a bare /dole/i here would steal Pineapple's "selling out fast".
     Keep every match narrow enough that exactly one entry can ever hit. */
  var BADGES = [
    { match: /dole\s*whip.*pineapple/i,  label: "selling out fast", cls: "vb-urgent", where: "both" },
    { match: /dole\s*whip.*strawberry/i, label: "exclusive collab", cls: "vb-collab", where: "hero"  },
    { match: /ihop/i,                     label: "exclusive collab", cls: "vb-collab", where: "hero"  }
  ];

  var ONCE_MISSING = [
    "subscriber pricing",
    "flavor swapping",
    "free gift",
    "free shipping when you subscribe"
  ];

  var SUB_ORDER = [
    { move: /30-day money-back/i, before: /pause or cancel/i }
  ];

  var SUB_EXTRA = [
    "Swap flavors anytime"
  ];

  var CTA_IDS = ["vpAtc", "vpStickyBtn"];

  var LEAD = /ships in (?:about\s*|~\s*)?60\s*days/gi;
  var LEAD_TO = "ships in 30 days";
  var GIFTCARD = /\s*Comes with a free[^.]*gift card[^.]*\.\s*/gi;

  function pick(name){
    var hit = null;
    BADGES.forEach(function(b){ if(b.match.test(name)) hit = b; });
    return hit;
  }

  function bagCount(){
    return [].slice.call(document.querySelectorAll("#vpChips .vp-step b"))
      .reduce(function(a, b){ return a + (parseInt(b.textContent, 10) || 0); }, 0);
  }

  function label(){
    ["vpAtcLabel", "vpStickyLbl"].forEach(function(id){
      var el = document.getElementById(id);
      if(el && FROM.test(el.textContent || "")) el.textContent = TO;
    });
  }

  function chipBadges(){
    var host = document.getElementById("vpChips");
    if(!host) return;
    [].slice.call(host.querySelectorAll(".vp-chip")).forEach(function(chip){
      var nm = chip.querySelector(".nm");
      var b = pick((nm && nm.textContent) || "");
      if(!b) return;
      if(b.where === "hero") return;              /* hero image only */
      if(chip.querySelector(".vb-tag")) return;
      var ctl = chip.querySelector(".vp-rowctl");
      if(!ctl) return;
      var tags = ctl.querySelector(".vp-tags");
      if(!tags){
        tags = document.createElement("span");
        tags.className = "vp-tags";
        ctl.insertBefore(tags, ctl.firstChild);
      }
      var t = document.createElement("span");
      t.className = "vb-tag " + b.cls;
      t.textContent = b.label;
      tags.appendChild(t);
    });
  }

  function heroBadge(){
    var stage = document.getElementById("vpStage");
    if(!stage) return;
    var img = stage.querySelector("#vpBag");
    var b = pick((img && img.getAttribute("alt")) || "");
    var mine = stage.querySelector(".vb.vb-urgent, .vb.vb-collab");
    if(!b || stage.querySelector(".vb:not(.vb-urgent):not(.vb-collab)")){
      if(mine) mine.parentNode.removeChild(mine);
      return;
    }
    if(mine){
      /* Switching flavour reuses the node, so the VARIANT CLASS has to move
         too -- otherwise "exclusive collab" keeps whatever colour the previous
         flavour's badge had. */
      if(mine.textContent !== b.label) mine.textContent = b.label;
      if(mine.className !== "vb " + b.cls) mine.className = "vb " + b.cls;
      return;
    }
    var s = document.createElement("span");
    s.className = "vb " + b.cls;
    s.textContent = b.label;
    stage.appendChild(s);
  }

  function findLi(ul, re){
    var hit = null;
    [].slice.call(ul.children).forEach(function(li){
      if(!hit && re.test(li.textContent || "")) hit = li;
    });
    return hit;
  }

  function perksCopy(){
    var ul = document.getElementById("vpPerks");
    var once = document.getElementById("vpOptOnce");
    var sub = document.getElementById("vpOptSub");
    if(!ul || !once || !sub) return;

    if(once.getAttribute("aria-checked") === "true"){
      var items = [].slice.call(ul.children);
      var matches = items.length === ONCE_MISSING.length && items.every(function(li, i){
        return li.classList.contains("no") &&
               li.textContent.trim().toLowerCase() === ONCE_MISSING[i];
      });
      if(matches) return;
      ul.innerHTML = "";
      ONCE_MISSING.forEach(function(t){
        var li = document.createElement("li");
        li.className = "no";
        li.textContent = t;
        ul.appendChild(li);
      });
      return;
    }

    if(sub.getAttribute("aria-checked") !== "true") return;

    SUB_ORDER.forEach(function(rule){
      var a = findLi(ul, rule.move);
      var b = findLi(ul, rule.before);
      if(!a || !b || a === b) return;
      if(b.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_FOLLOWING) ul.insertBefore(a, b);
    });

    var withImg = ul.querySelector("li.has-img");
    SUB_EXTRA.forEach(function(t){
      var key = t.trim().toLowerCase();
      var already = [].slice.call(ul.children).some(function(li){
        return li.textContent.trim().toLowerCase() === key;
      });
      if(already) return;
      var li = document.createElement("li");
      li.textContent = t;
      if(withImg) ul.insertBefore(li, withImg);
      else ul.appendChild(li);
    });
  }

  var wired = false;
  function wireSubCard(){
    if(wired) return;
    var info = document.querySelector(".vp-info");
    if(!info) return;
    wired = true;
    info.addEventListener("click", function(e){
      var sub = document.getElementById("vpOptSub");
      if(!sub) return;
      var card = sub.closest(".vp-grp");
      if(!card || !card.contains(e.target)) return;
      if(e.target.closest("#vpOptOnce")) return;
      if(e.target.closest("#vpOptSub")) return;
      if(e.target.closest("a,button,input,select,textarea,label,video")) return;
      if(sub.getAttribute("aria-checked") === "true") return;
      sub.click();
    });
  }

  function headerOffset(){
    var best = 0;
    [].slice.call(document.querySelectorAll("header, .header, [class*='header']")).forEach(function(el){
      var s = getComputedStyle(el);
      if(s.position !== "fixed" && s.position !== "sticky") return;
      var r = el.getBoundingClientRect();
      if(r.top <= 1 && r.height > best && r.height < 200) best = r.height;
    });
    return best || 80;
  }

  function reducedMotion(){
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch(e){ return false; }
  }

  function scrollToChips(){
    var chips = document.getElementById("vpChips");
    if(!chips) return;
    var r = chips.getBoundingClientRect();
    if(r.top >= 0 && r.top < window.innerHeight * 0.5) return;   /* already there */
    var y = Math.max(0, window.scrollY + r.top - headerOffset() - 12);

    if(reducedMotion() || document.hidden){
      window.scrollTo(0, y);
      return;
    }
    var from = window.scrollY;
    try { window.scrollTo({ top: y, behavior: "smooth" }); }
    catch(e){ window.scrollTo(0, y); return; }

    /* Smooth scrolling silently no-ops in some states (hidden tab, no support).
       If nothing has moved shortly after, jump instead -- better an abrupt
       scroll than a button that appears to do nothing. */
    setTimeout(function(){
      if(Math.abs(window.scrollY - from) < 4) window.scrollTo(0, y);
    }, 250);
  }

  function hitsCta(x, y){
    var hit = false;
    CTA_IDS.forEach(function(id){
      if(hit) return;
      var b = document.getElementById(id);
      if(!b) return;
      var r = b.getBoundingClientRect();
      if(!r.width || !r.height) return;
      if(x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) hit = true;
    });
    return hit;
  }

  var emptyWired = false;
  function wireEmptyCta(){
    if(emptyWired) return;
    emptyWired = true;
    document.addEventListener("click", function(e){
      if(bagCount() > 0) return;                  /* a real add-to-cart */
      if(!hitsCta(e.clientX, e.clientY)) return;
      scrollToChips();
    });
  }

  function preorder(){
    ["vpPre", "vpStage"].forEach(function(id){
      var host = document.getElementById(id);
      if(!host) return;
      var walk = document.createTreeWalker(host, NodeFilter.SHOW_TEXT), n;
      while((n = walk.nextNode())){
        var v = n.nodeValue;
        if(!v) continue;
        var out = v.replace(LEAD, LEAD_TO).replace(GIFTCARD, "");
        if(out !== v) n.nodeValue = out;
      }
    });
  }

  var timer = null, obs = null;
  function watch(){
    ["vpAtcLabel", "vpStickyLbl", "vpChips", "vpPre", "vpStage", "vpPerks"].forEach(function(id){
      var n = document.getElementById(id);
      if(n) obs.observe(n, {childList:true, subtree:true, characterData:true});
    });
  }
  function run(){
    if(obs) obs.disconnect();
    try { label(); chipBadges(); heroBadge(); perksCopy(); wireSubCard(); wireEmptyCta(); }
    catch(e){ /* must never take the buy box down */ }
    finally { if(obs) watch(); }
  }

  obs = new MutationObserver(function(){
    try { label(); } catch(e){}
    clearTimeout(timer);
    timer = setTimeout(run, 40);
  });

  if(document.readyState === "loading"){
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
  window.addEventListener("load", run);
})();